import axios from 'axios';
import { API_URL } from '../config/api';
import { uploadFilesWithProgress } from './uploadFilesWithProgress';

export const likedByIds = (attachment) =>
  Array.isArray(attachment?.likedByUserIds) ? attachment.likedByUserIds : [];

export const isAttachmentLikedBy = (attachment, userId) =>
  Boolean(userId && likedByIds(attachment).some((id) => String(id) === String(userId)));

export function toggleLikedByUserIds(likedByUserIds, userId) {
  const ids = Array.isArray(likedByUserIds) ? [...likedByUserIds] : [];
  if (!userId) return ids;
  const has = ids.some((id) => String(id) === String(userId));
  if (has) return ids.filter((id) => String(id) !== String(userId));
  ids.push(userId);
  return ids;
}

export async function toggleAttachmentLike(messageId, attachmentId) {
  const token = localStorage.getItem('token');
  const { data } = await axios.post(
    `${API_URL}/messages/${messageId}/attachments/${attachmentId}/like`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
}

export async function uploadImageAsAttachment(file, previousAttachment = null) {
  const token = localStorage.getItem('token');
  const [uploaded] = await uploadFilesWithProgress({
    apiUrl: API_URL,
    token,
    files: [file],
  });
  const fileUrl = uploaded?.data?.fileUrl;
  if (!fileUrl) {
    throw new Error('Upload failed');
  }
  const attachment = {
    fileName: uploaded?.data?.fileName || file.name,
    fileUrl,
    fileType: 'IMAGE',
    fileSize: file.size,
  };
  const priorUrl = previousAttachment?.previousFileUrl || previousAttachment?.fileUrl;
  if (priorUrl && !String(priorUrl).startsWith('blob:') && !String(priorUrl).startsWith('data:')) {
    attachment.previousFileUrl = priorUrl;
    attachment.previousFileName =
      previousAttachment?.previousFileName || previousAttachment?.fileName || undefined;
  }
  return attachment;
}

/**
 * Upload an edited image and replace the existing attachment in-place
 * so the same message can be edited again later.
 */
export async function uploadAndReplaceAttachmentImage({
  messageId,
  attachmentId,
  file,
  previousAttachment = null,
}) {
  if (!messageId || !attachmentId) {
    throw new Error('Message and attachment are required to replace an image');
  }
  const uploaded = await uploadImageAsAttachment(file, previousAttachment);
  const token = localStorage.getItem('token');
  const { data } = await axios.patch(
    `${API_URL}/messages/${messageId}/attachments/${attachmentId}/file`,
    {
      fileUrl: uploaded.fileUrl,
      fileName: uploaded.fileName,
      fileSize: uploaded.fileSize || file.size,
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
}

/** Build a local edited attachment while preserving the prior version for compare tabs. */
export function withPreviousVersion(previousAttachment, file, previewUrl) {
  return {
    ...previousAttachment,
    file,
    fileName: file.name,
    fileType: 'IMAGE',
    fileSize: file.size,
    fileUrl: previewUrl,
    // Keep the earliest prior version across repeated edits.
    previousFileUrl: previousAttachment?.previousFileUrl || previousAttachment?.fileUrl,
    previousFileName: previousAttachment?.previousFileName || previousAttachment?.fileName,
  };
}
